import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Science as ScienceIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { getAuthHeadersSync } from '../../../../shared/utils/authHelpers';

interface Dataset {
  dataset_id: string;
  tenant_id: string | null;
  name: string;
  type: string;
  s3_path: string;
  size_bytes: number | null;
  sample_count: number | null;
  feature_count: number | null;
  status: string;
  created_at: string;
  description: string | null;
}

interface DatasetListResponse {
  datasets: Dataset[];
  total: number;
}

export const DatasetManagement: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('shared');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<Dataset | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Synthetic data dialog state
  const [syntheticDialogOpen, setSyntheticDialogOpen] = useState(false);
  const [syntheticSampleCount, setSyntheticSampleCount] = useState(10000);
  const [syntheticFeatureCount, setSyntheticFeatureCount] = useState(78);
  const [syntheticAttackRatio, setSyntheticAttackRatio] = useState(0.3);
  const [syntheticName, setSyntheticName] = useState('synthetic');
  const [generatingSynthetic, setGeneratingSynthetic] = useState(false);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/platform-admin/datasets`, {
        headers: getAuthHeadersSync()
      });
      
      if (response.ok) {
        const data: DatasetListResponse = await response.json();
        setDatasets(data.datasets);
      } else {
        setError('Failed to load datasets');
      }
    } catch (err) {
      setError('Error loading datasets: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    setUploadFile(null);
    setUploadName('');
    setUploadType('shared');
    setUploadDescription('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadName) {
        setUploadName(file.name.replace(/\.(csv|parquet)$/i, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName) {
      setError('Please select a file and provide a name');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const formData = new FormData();
      formData.append('file', uploadFile);

      const params = new URLSearchParams({
        name: uploadName,
        type: uploadType,
        ...(uploadDescription && { description: uploadDescription })
      });

      const response = await fetch(`${baseUrl}/api/platform-admin/datasets/upload?${params}`, {
        method: 'POST',
        headers: {
          ...getAuthHeadersSync(),
          // Don't set Content-Type, let browser set it with boundary for multipart/form-data
        },
        body: formData
      });

      if (response.ok) {
        setSuccess('Dataset uploaded successfully');
        setUploadDialogOpen(false);
        loadDatasets();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to upload dataset');
      }
    } catch (err) {
      setError('Error uploading dataset: ' + (err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (dataset: Dataset) => {
    setDatasetToDelete(dataset);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!datasetToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/platform-admin/datasets/${datasetToDelete.dataset_id}`, {
        method: 'DELETE',
        headers: getAuthHeadersSync()
      });

      if (response.ok) {
        setSuccess('Dataset deleted successfully');
        setDeleteDialogOpen(false);
        loadDatasets();
      } else {
        setError('Failed to delete dataset');
      }
    } catch (err) {
      setError('Error deleting dataset: ' + (err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateSynthetic = async () => {
    setGeneratingSynthetic(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/platform-admin/datasets/generate-synthetic`, {
        method: 'POST',
        headers: {
          ...getAuthHeadersSync(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sample_count: syntheticSampleCount,
          feature_count: syntheticFeatureCount,
          attack_ratio: syntheticAttackRatio,
          name: syntheticName
        })
      });

      if (response.ok) {
        setSuccess('Synthetic dataset generated successfully');
        setSyntheticDialogOpen(false);
        loadDatasets();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to generate synthetic dataset');
      }
    } catch (err) {
      setError('Error generating synthetic dataset: ' + (err as Error).message);
    } finally {
      setGeneratingSynthetic(false);
    }
  };

  const formatBytes = (bytes: number | null): string => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'ready': return 'success';
      case 'processing': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'default' => {
    switch (type) {
      case 'shared': return 'primary';
      case 'feedback': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dataset Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ScienceIcon />}
            onClick={() => setSyntheticDialogOpen(true)}
          >
            Generate Synthetic
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleUploadClick}
          >
            Upload Dataset
          </Button>
          <IconButton onClick={loadDatasets} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : datasets.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No datasets found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upload a dataset or generate synthetic data to get started
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right">Samples</TableCell>
                    <TableCell align="right">Features</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datasets.map((dataset) => (
                    <TableRow key={dataset.dataset_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {dataset.name}
                        </Typography>
                        {dataset.description && (
                          <Typography variant="caption" color="text.secondary">
                            {dataset.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={dataset.type}
                          size="small"
                          color={getTypeColor(dataset.type)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={dataset.status}
                          size="small"
                          color={getStatusColor(dataset.status)}
                        />
                      </TableCell>
                      <TableCell align="right">{formatBytes(dataset.size_bytes)}</TableCell>
                      <TableCell align="right">
                        {dataset.sample_count?.toLocaleString() || 'N/A'}
                      </TableCell>
                      <TableCell align="right">{dataset.feature_count || 'N/A'}</TableCell>
                      <TableCell>{formatDate(dataset.created_at)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(dataset)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Dataset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              {uploadFile ? uploadFile.name : 'Select File (CSV or Parquet)'}
              <input
                type="file"
                hidden
                accept=".csv,.parquet"
                onChange={handleFileChange}
              />
            </Button>

            <TextField
              label="Dataset Name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                label="Type"
              >
                <MenuItem value="shared">Shared</MenuItem>
                <MenuItem value="feedback">Feedback</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !uploadFile || !uploadName}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Dataset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete dataset &ldquo;{datasetToDelete?.name}&rdquo;?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Synthetic Dialog */}
      <Dialog open={syntheticDialogOpen} onClose={() => setSyntheticDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Synthetic Dataset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Dataset Name"
              value={syntheticName}
              onChange={(e) => setSyntheticName(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Sample Count"
              type="number"
              value={syntheticSampleCount}
              onChange={(e) => setSyntheticSampleCount(Number(e.target.value))}
              fullWidth
              required
            />

            <TextField
              label="Feature Count"
              type="number"
              value={syntheticFeatureCount}
              onChange={(e) => setSyntheticFeatureCount(Number(e.target.value))}
              fullWidth
              required
            />

            <TextField
              label="Attack Ratio (0.0 - 1.0)"
              type="number"
              value={syntheticAttackRatio}
              onChange={(e) => setSyntheticAttackRatio(Number(e.target.value))}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyntheticDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateSynthetic}
            variant="contained"
            disabled={generatingSynthetic}
          >
            {generatingSynthetic ? <CircularProgress size={24} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
